/**
 * The contacts dictionary comes in three variants, each loaded from a
 * different endpoint and stored under its own name in `IDictionary`:
 *
 * `contacts` - GET /contacts/names, giving id, email, fullName and companyId.
 * `contactsFull` - GET /contacts, giving the full IEventContact.
 * `contactsExtended` - GET /contacts?includeAssets, adding assets.
 *
 * A config declares the one it needs; consumers look the options up by that
 * same name, so nothing is aliased or duplicated.
 *
 * Keep this module free of imports - it must stay unit-testable.
 */

export const CONTACTS_DICTIONARY = 'contacts';

/** Ordered light -> rich; the index is the richness rank. */
export const CONTACT_DICTIONARIES = [
  'contacts',
  'contactsFull',
  'contactsExtended'
] as const;

export type ContactsDictionaryName = (typeof CONTACT_DICTIONARIES)[number];

export function isContactsDictionary(
  name?: string
): name is ContactsDictionaryName {
  return !!name && (CONTACT_DICTIONARIES as readonly string[]).includes(name);
}

/**
 * The richest contacts variant declared in `names`, or the light default when
 * none is. Richness matters only for a config declaring more than one variant:
 * the richer payload is a superset, so it satisfies the lighter declaration too.
 */
export function pickContactsDictionary(
  names: string[]
): ContactsDictionaryName {
  return names.reduce<ContactsDictionaryName>((richest, name) => {
    if (!isContactsDictionary(name)) {
      return richest;
    }
    return CONTACT_DICTIONARIES.indexOf(name) >
      CONTACT_DICTIONARIES.indexOf(richest)
      ? name
      : richest;
  }, CONTACTS_DICTIONARY);
}
